import { useEffect, useRef, useState } from "react";
import { CloudRain, Waves, Flame, Music, VolumeX, Volume1 } from "lucide-react";

/**
 * AmbientSounds — procedural cozy background layers for narration.
 * All sounds are synthesized live via Web Audio (no external files) so they
 * loop forever, respect CORS, and cost zero bandwidth.
 *
 * Props:
 *  - active: boolean — whether narration is currently playing (auto-mutes when false)
 */
const PRESETS = [
  { id: "off", name: "Off", icon: VolumeX },
  { id: "rain", name: "Rain", icon: CloudRain },
  { id: "ocean", name: "Ocean", icon: Waves },
  { id: "fire", name: "Fire", icon: Flame },
  { id: "chimes", name: "Music Box", icon: Music },
];

const STORAGE_PRESET = "dw-ambient-preset";
const STORAGE_VOLUME = "dw-ambient-volume";

// ── Noise buffer helpers ─────────────────────────────────────────────────────
function makeNoiseBuffer(ctx, kind) {
  const seconds = 4;
  const buffer = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  if (kind === "white") {
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
  } else if (kind === "pink") {
    // Paul Kellet pink-noise algo
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
  } else if (kind === "brown") {
    let lastOut = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      lastOut = (lastOut + 0.02 * white) / 1.02;
      data[i] = lastOut * 3.5;
    }
  }
  return buffer;
}

// ── Preset builders ──────────────────────────────────────────────────────────
function startRain(ctx, out) {
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer(ctx, "pink");
  src.loop = true;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass"; lp.frequency.value = 1600; lp.Q.value = 0.7;
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass"; hp.frequency.value = 300;
  src.connect(hp); hp.connect(lp); lp.connect(out);
  src.start();
  return () => { try { src.stop(); } catch (e) { /* ignore */ } };
}

function startOcean(ctx, out) {
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer(ctx, "brown");
  src.loop = true;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass"; lp.frequency.value = 700;
  // Slow amplitude LFO to simulate waves
  const gain = ctx.createGain();
  gain.gain.value = 0.5;
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.12; // ~8s per wave
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.35;
  lfo.connect(lfoGain); lfoGain.connect(gain.gain);
  src.connect(lp); lp.connect(gain); gain.connect(out);
  src.start(); lfo.start();
  return () => { try { src.stop(); lfo.stop(); } catch (e) { /* ignore */ } };
}

function startFire(ctx, out) {
  // Base warm noise
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer(ctx, "pink");
  src.loop = true;
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass"; lp.frequency.value = 900;
  const baseGain = ctx.createGain();
  baseGain.gain.value = 0.55;
  src.connect(lp); lp.connect(baseGain); baseGain.connect(out);
  src.start();

  // Random crackle bursts
  let cancelled = false;
  const crackle = () => {
    if (cancelled) return;
    const now = ctx.currentTime;
    const dur = 0.04 + Math.random() * 0.06;
    const burst = ctx.createBufferSource();
    burst.buffer = makeNoiseBuffer(ctx, "white");
    const bf = ctx.createBiquadFilter();
    bf.type = "bandpass"; bf.frequency.value = 1500 + Math.random() * 2500; bf.Q.value = 4;
    const bg = ctx.createGain();
    bg.gain.setValueAtTime(0, now);
    bg.gain.linearRampToValueAtTime(0.6, now + 0.005);
    bg.gain.exponentialRampToValueAtTime(0.001, now + dur);
    burst.connect(bf); bf.connect(bg); bg.connect(out);
    burst.start(now); burst.stop(now + dur + 0.02);
    setTimeout(crackle, 150 + Math.random() * 800);
  };
  crackle();
  return () => { cancelled = true; try { src.stop(); } catch (e) { /* ignore */ } };
}

function startChimes(ctx, out) {
  // Gentle pentatonic music-box tinkles
  const scale = [523.25, 587.33, 659.25, 783.99, 880.00, 1046.50]; // C5 D5 E5 G5 A5 C6
  let cancelled = false;
  const chime = () => {
    if (cancelled) return;
    const now = ctx.currentTime;
    const freq = scale[Math.floor(Math.random() * scale.length)];
    const osc = ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.28, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, now + 2.4);
    // Soft second harmonic for warmth
    const osc2 = ctx.createOscillator();
    osc2.type = "sine";
    osc2.frequency.value = freq * 2;
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0, now);
    g2.gain.linearRampToValueAtTime(0.09, now + 0.03);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 1.8);
    osc.connect(g); osc2.connect(g2);
    g.connect(out); g2.connect(out);
    osc.start(now); osc2.start(now);
    osc.stop(now + 2.6); osc2.stop(now + 2.0);
    setTimeout(chime, 1200 + Math.random() * 2400);
  };
  chime();
  return () => { cancelled = true; };
}

const BUILDERS = { rain: startRain, ocean: startOcean, fire: startFire, chimes: startChimes };

export default function AmbientSounds({ active }) {
  const [preset, setPreset] = useState(() => localStorage.getItem(STORAGE_PRESET) || "off");
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem(STORAGE_VOLUME) || "0.35");
    return isNaN(v) ? 0.35 : v;
  });
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const stopRef = useRef(null);

  // Persist choices
  useEffect(() => { localStorage.setItem(STORAGE_PRESET, preset); }, [preset]);
  useEffect(() => { localStorage.setItem(STORAGE_VOLUME, String(volume)); }, [volume]);

  // Reflect volume in master gain
  useEffect(() => {
    if (masterRef.current && ctxRef.current) {
      masterRef.current.gain.setTargetAtTime(active && preset !== "off" ? volume : 0, ctxRef.current.currentTime, 0.4);
    }
  }, [volume, active, preset]);

  // Build/tear down sound graph on preset change
  useEffect(() => {
    // Stop previous
    if (stopRef.current) { stopRef.current(); stopRef.current = null; }
    if (preset === "off") return;
    // Lazy-init audio context
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctxRef.current = new AC();
      const master = ctxRef.current.createGain();
      master.gain.value = 0;
      master.connect(ctxRef.current.destination);
      masterRef.current = master;
    }
    // Ensure resumed
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    const builder = BUILDERS[preset];
    if (builder) {
      stopRef.current = builder(ctxRef.current, masterRef.current);
      masterRef.current.gain.setTargetAtTime(active ? volume : 0, ctxRef.current.currentTime, 0.4);
    }
    return () => {
      if (stopRef.current) { stopRef.current(); stopRef.current = null; }
    };
  }, [preset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => () => {
    if (stopRef.current) { try { stopRef.current(); } catch (e) { /* ignore */ } stopRef.current = null; }
    const ctx = ctxRef.current;
    ctxRef.current = null;
    masterRef.current = null;
    if (ctx && ctx.state !== "closed") {
      // .close() is async → guard both sync throws and async rejections (StrictMode double-cleanup)
      Promise.resolve().then(() => ctx.close()).catch(() => {});
    }
  }, []);

  return (
    <div data-testid="ambient-sounds" className="mt-4 pt-4 border-t border-purple-500/15">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Ambient</p>
        {preset !== "off" && (
          <div className="flex items-center gap-1.5">
            <Volume1 size={11} className="text-slate-500" />
            <input
              data-testid="ambient-volume"
              type="range" min={0} max={1} step={0.05}
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="audio-progress w-16 h-1.5"
              style={{ background: `linear-gradient(to right, rgba(251,191,36,0.6) ${volume * 100}%, rgba(255,255,255,0.15) 0%)` }}
            />
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {PRESETS.map((p) => {
          const Icon = p.icon;
          const isActive = preset === p.id;
          return (
            <button
              key={p.id}
              data-testid={`ambient-${p.id}`}
              type="button"
              onClick={() => setPreset(p.id)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-medium transition-colors ${
                isActive
                  ? "bg-amber-500/20 border border-amber-400/40 text-amber-300"
                  : "border border-purple-500/20 text-slate-400 hover:text-white hover:border-purple-500/40"
              }`}
            >
              <Icon size={11} />
              {p.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
